/**
 * The specialty chatbots at /all, /neuro and /derma were retired: the root chat
 * covers every specialty. nginx redirects these paths with a 302; this does the
 * same in the browser for the Vite dev server and anything else that serves
 * index.html without nginx.
 */
const RETIRED_PREFIX = /^\/(?:all|neuro|derma)(?=\/|$)/i;

/**
 * Some printed QR codes carry ?location=225 or ?flyer=1320 instead of
 * ?source=location_225. Source tracking reads only `source`, so these map onto
 * it. Listed in priority order for a link that has both.
 */
const LEGACY_SOURCE_PARAMS = ['location', 'flyer'] as const;
const LEGACY_SOURCE_ID = /^\d+$/;

/** The query with ?location=N or ?flyer=N moved into ?source=…, or null. */
export function legacySourceSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  const present = LEGACY_SOURCE_PARAMS.filter((name) => params.has(name));
  if (present.length === 0) return null;

  if (!params.has('source')) {
    const name = present.find((n) => LEGACY_SOURCE_ID.test(params.get(n)!));
    if (!name) return null;
    params.set('source', `${name}_${params.get(name)}`);
  }
  for (const name of present) params.delete(name);
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Where a retired chatbot path or legacy QR link lives now, or null. */
export function retiredChatRedirect(
  pathname: string,
  search: string,
  hash: string,
): string | null {
  const retired = RETIRED_PREFIX.test(pathname);
  const fixedSearch = legacySourceSearch(search);
  if (!retired && fixedSearch === null) return null;
  const path = retired ? pathname.replace(RETIRED_PREFIX, '') || '/' : pathname;
  return path + (fixedSearch ?? search) + hash;
}

/** Rewrite the address before the router and source tracking read it. */
export function redirectRetiredChatPath(): void {
  if (typeof window === 'undefined') return;
  const { pathname, search, hash } = window.location;
  const target = retiredChatRedirect(pathname, search, hash);
  if (target) window.history.replaceState(window.history.state, '', target);
}
